import { T, TFunction } from '@transifex/react'
import React from 'react'
import { isReturn } from '../../../functions/sale'
import { SaleStatus } from '../../../models/sale.constants'
import { ReduxCurrentSale } from '../../../state/slices/currentSale/currentSaleSlice'

export enum TooltipContext {
  DISCOUNT = 'Discount',
  PROMOTION = 'Promo code',
}

const DISCOUNT_TOOLTIP_HEADER_CONTEXT = 'Discount disabled tooltip header text'
const DISCOUNT_TOOLTIP_MESSAGE_CONTEXT = 'Discount disabled tooltip message'
const PROMO_CODE_TOOLTIP_HEADER_CONTEXT =
  'Promo code disabled tooltip header text'
const PROMO_CODE_TOOLTIP_MESSAGE_CONTEXT = 'Promo code disabled tooltip message'

type TooltipMessage = {
  title: string
  message: React.JSX.Element
}

/**
 * Builds a disabled add-row tooltip from a translated title and a message element.
 *
 * Translation calls stay at the call site so Transifex can statically extract
 * each literal source string and context.
 */
const createTooltipMessage = (
  title: string,
  message: React.JSX.Element
): TooltipMessage => ({ title, message })

export const getDisabledTooltipMessage = (
  t: TFunction,
  currentSale: ReduxCurrentSale,
  context: TooltipContext
) => {
  if (isReturn(currentSale)) {
    if (context === TooltipContext.DISCOUNT) {
      return createTooltipMessage(
        t('Sale is being returned', {
          _context: DISCOUNT_TOOLTIP_HEADER_CONTEXT,
        }),
        <T
          _str="Discount can’t be added because the sale is being returned."
          _context={DISCOUNT_TOOLTIP_MESSAGE_CONTEXT}
        />
      )
    }

    return createTooltipMessage(
      t('Sale is being returned', {
        _context: PROMO_CODE_TOOLTIP_HEADER_CONTEXT,
      }),
      <T
        _str="Promo code can’t be added because the sale is being returned."
        _context={PROMO_CODE_TOOLTIP_MESSAGE_CONTEXT}
      />
    )
  }

  if (!currentSale.status) {
    return
  }

  const messages: Partial<Record<SaleStatus, TooltipMessage>> =
    context === TooltipContext.DISCOUNT
      ? {
          [SaleStatus.AWAITING_DISPATCH]: createTooltipMessage(
            t('Sale has items for fulfillment', {
              _context: DISCOUNT_TOOLTIP_HEADER_CONTEXT,
            }),
            <T
              _str="Discount can’t be added to the sale because there are items for fulfillment."
              _context={DISCOUNT_TOOLTIP_MESSAGE_CONTEXT}
            />
          ),
          [SaleStatus.AWAITING_PICKUP]: createTooltipMessage(
            t('Sale has items for fulfillment', {
              _context: DISCOUNT_TOOLTIP_HEADER_CONTEXT,
            }),
            <T
              _str="Discount can’t be added to the sale because there are items for fulfillment."
              _context={DISCOUNT_TOOLTIP_MESSAGE_CONTEXT}
            />
          ),
          [SaleStatus.ACCOUNT_SALE]: createTooltipMessage(
            t('Sale is on account', {
              _context: DISCOUNT_TOOLTIP_HEADER_CONTEXT,
            }),
            <T
              _str="Discount can’t be added because the sale is on account."
              _context={DISCOUNT_TOOLTIP_MESSAGE_CONTEXT}
            />
          ),
          [SaleStatus.LAYBY_SALE]: createTooltipMessage(
            t('Sale is on layby/layaway', {
              _context: DISCOUNT_TOOLTIP_HEADER_CONTEXT,
            }),
            <T
              _str="Discount can’t be added because the sale is on layby/layaway."
              _context={DISCOUNT_TOOLTIP_MESSAGE_CONTEXT}
            />
          ),
        }
      : {
          [SaleStatus.AWAITING_DISPATCH]: createTooltipMessage(
            t('Sale has items for fulfillment', {
              _context: PROMO_CODE_TOOLTIP_HEADER_CONTEXT,
            }),
            <T
              _str="Promo code can’t be added to the sale because there are items for fulfillment."
              _context={PROMO_CODE_TOOLTIP_MESSAGE_CONTEXT}
            />
          ),
          [SaleStatus.AWAITING_PICKUP]: createTooltipMessage(
            t('Sale has items for fulfillment', {
              _context: PROMO_CODE_TOOLTIP_HEADER_CONTEXT,
            }),
            <T
              _str="Promo code can’t be added to the sale because there are items for fulfillment."
              _context={PROMO_CODE_TOOLTIP_MESSAGE_CONTEXT}
            />
          ),
          [SaleStatus.ACCOUNT_SALE]: createTooltipMessage(
            t('Sale is on account', {
              _context: PROMO_CODE_TOOLTIP_HEADER_CONTEXT,
            }),
            <T
              _str="Promo code can’t be added because the sale is on account."
              _context={PROMO_CODE_TOOLTIP_MESSAGE_CONTEXT}
            />
          ),
          [SaleStatus.LAYBY_SALE]: createTooltipMessage(
            t('Sale is on layby/layaway', {
              _context: PROMO_CODE_TOOLTIP_HEADER_CONTEXT,
            }),
            <T
              _str="Promo code can’t be added because the sale is on layby/layaway."
              _context={PROMO_CODE_TOOLTIP_MESSAGE_CONTEXT}
            />
          ),
        }

  return messages[currentSale.status]
}
